import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import useNotify from '../../hooks/useNotify';
import {
    LINK_BASIS,
    LINK_ITEM,
    VISIT_END,
    apiError,
    gateLabel,
    imageUrl,
    saveLink,
} from '../../helpers/parkingEvents';
import DiffPlate, { plateSx } from './DiffPlate';
import Reasons from './Reasons';

const huf = (n) => `${new Intl.NumberFormat('hu-HU').format(n || 0)} Ft`;

function Side({ title, img, plate, lines }) {
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
                {title}
            </Typography>
            <Box
                sx={{
                    aspectRatio: '16 / 9',
                    bgcolor: img ? '#000' : 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                }}
            >
                {img ? (
                    <Box
                        component="img"
                        src={img}
                        alt={title}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        nincs kép
                    </Typography>
                )}
            </Box>
            <Typography sx={{ ...plateSx, fontSize: 20 }}>{plate}</Typography>
            {lines.filter(Boolean).map((l) => (
                <Typography key={l} variant="body2" color="text.secondary">
                    {l}
                </Typography>
            ))}
        </Box>
    );
}

/**
 * The guard's decision about a pair: this parking ↔ an item (plate variant, release, exit read, other record).
 * Two columns, the reasons as ✓ / ✗ lines, what was checked, a note; link or "not the same car".
 * @param {object} visit the parking (summary)
 * @param {object} item {type, ref, plate, title, lines, img}
 */
export default function LinkDialog({
    open,
    visit,
    item,
    diff,
    reasons,
    canReject,
    onClose,
    onDone,
}) {
    const { notifyError, notifySuccess } = useNotify();
    const [basis, setBasis] = useState([]);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setBasis([]);
            setNote('');
        }
    }, [open]);

    if (!visit || !item) return null;
    const valid = basis.length > 0 || note.trim().length >= 3;
    const end = VISIT_END[visit.end] || { label: visit.end, color: 'default' };

    const send = (action) => {
        if (saving) return;
        setSaving(true);
        saveLink({
            action,
            visitId: visit.id,
            itemType: item.type,
            itemRef: item.ref,
            basis,
            note: note.trim(),
            reasons: reasons || [],
        })
            .then((link) => {
                notifySuccess(
                    action === 'link' ? 'Összekapcsolva' : 'Rögzítve: nem ugyanaz az autó'
                );
                onDone(link, action);
            })
            .catch((e) => notifyError(apiError(e)))
            .finally(() => setSaving(false));
    };

    const toggle = (k) => setBasis((b) => (b.includes(k) ? b.filter((x) => x !== k) : [...b, k]));

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>Összekapcsolás – {LINK_ITEM[item.type]}</DialogTitle>
            <DialogContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Side
                        title="Parkolás"
                        img={imageUrl(visit.in)}
                        plate={<DiffPlate plate={visit.plate || '–'} diff={diff} />}
                        lines={[
                            `behajtás ${visit.in.time.slice(0, 16)} · ${gateLabel(visit.in.gate)}`,
                            `${end.label}${
                                visit.paid ? ` · fizetve ${huf(visit.paid)}` : ' · nincs fizetés'
                            }`,
                            visit.card ? `bérletkártya ${visit.card}` : null,
                        ]}
                    />
                    <Side
                        title={item.title}
                        img={item.img}
                        plate={item.plate || '–'}
                        lines={item.lines || []}
                    />
                </Stack>
                {reasons && reasons.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Miért lehet ugyanaz az autó
                        </Typography>
                        <Reasons reasons={reasons} />
                    </Box>
                )}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                    Mit ellenőriztél?
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(LINK_BASIS).map(([k, label]) => (
                        <Chip
                            key={k}
                            label={label}
                            color={basis.includes(k) ? 'primary' : 'default'}
                            variant={basis.includes(k) ? 'filled' : 'outlined'}
                            onClick={() => toggle(k)}
                        />
                    ))}
                </Stack>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Megjegyzés"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    sx={{ mt: 2 }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                    A kapcsolat csak a Backoffice-ban látszik: a be.web-ben a fizetést / kiengedést
                    ez nem rendezi, és a parkolást nem teszi fizetetté. Visszavonható.
                </Alert>
            </DialogContent>
            <DialogActions>
                {saving && <CircularProgress size={20} sx={{ mr: 1 }} />}
                <Button onClick={onClose} disabled={saving}>
                    Mégse
                </Button>
                {canReject && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => send('reject')}
                        disabled={saving}
                    >
                        Nem ugyanaz
                    </Button>
                )}
                <Button
                    variant="contained"
                    onClick={() => send('link')}
                    disabled={!valid || saving}
                >
                    Összekapcsolom
                </Button>
            </DialogActions>
        </Dialog>
    );
}
